import { z } from "zod";
import { api, mBaseURL } from "@/config/api";

import { APIResponseSuccessSchema } from "@/schema/api";
import { AuthenticationSchema, UserSchema } from "@/schema/authentication";
import { OrganizationSchema } from "@/schema/organization";
import { createURLPath } from "@/lib/utils";
import { persistAuthentication } from "@/lib/auth";

interface SendMagicLinkProps {
  email: string;
  origin?: string;
}

export const AuthApiFactory = {
  Endpoint: mBaseURL + "/api/auth",
  Schemas: {
    Response: {
      Authenticate: APIResponseSuccessSchema.extend({
        data: z.object({ user: UserSchema, organization: OrganizationSchema }),
      }),
      TokenPair: APIResponseSuccessSchema.extend({
        data: AuthenticationSchema,
      }),
      GoogleAuthorizationURL: APIResponseSuccessSchema.extend({
        data: z.object({ url: z.string().url() }),
      }),
      SendMagicLink: APIResponseSuccessSchema.extend({
        data: z.object({ is_new_user: z.boolean() }),
      }),
      AuthenticateWithGoogle: APIResponseSuccessSchema.extend({
        data: AuthenticationSchema.extend({
          is_new_user: z.boolean(),
        }),
      }),
    },
  },
  Apis: {
    Authenticate: async () => {
      const session = await api(createURLPath(AuthApiFactory.Endpoint, "session"));

      if (!session.ok) {
        const error = await session.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw error;
      }

      return session.json().then(AuthApiFactory.Schemas.Response.Authenticate.parse);
    },
    SendMagicLink: async ({ email, origin }: SendMagicLinkProps) => {
      const session = await api(createURLPath(AuthApiFactory.Endpoint, "magic-link", "send"), {
        method: "POST",
        body: JSON.stringify({ email, origin }),
      });

      if (!session.ok) {
        const error = await session.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw error;
      }

      return session
        .json()
        .then(AuthApiFactory.Schemas.Response.SendMagicLink.parse)
        .then((response) => response.data);
    },
    VerifyMagicLink: async (token: string) => {
      const verify = await api(createURLPath(AuthApiFactory.Endpoint, "magic-link", "verify"), {
        method: "POST",
        body: JSON.stringify({ token }),
      });

      if (!verify.ok) {
        const error = await verify.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw error;
      }

      const tokens = await verify.json().then(AuthApiFactory.Schemas.Response.TokenPair.parse);
      persistAuthentication(tokens.data);
      return tokens;
    },
    AuthorizeWithGoogle: async (redirect_uri: string, scope?: string) => {
      let url = createURLPath(AuthApiFactory.Endpoint, "google", "authorize?redirect_uri=" + encodeURIComponent(redirect_uri));
      if (scope) url += "&scope=" + encodeURIComponent(scope);
      const response = await api(url, {
        method: "POST",
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw error;
      }

      return response.json().then(AuthApiFactory.Schemas.Response.GoogleAuthorizationURL.parse);
    },
    AuthenticateWithGoogle: async (code: string, redirect_uri: string) => {
      const response = await api(createURLPath(AuthApiFactory.Endpoint, "google", "authenticate?code=" + code + "&redirect_uri=" + encodeURIComponent(redirect_uri)), {
        method: "POST",
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw error;
      }

      const tokens = await response
        .json()
        .then(AuthApiFactory.Schemas.Response.AuthenticateWithGoogle.parse)
        .then((response) => response.data);

      persistAuthentication(tokens);
      return tokens;
    },
  },
};
